import { ScaleLinear } from "d3-scale";
import { LinePath } from "@visx/shape";
import { Fragment, useCallback, useMemo } from "react";
import useChartingColors from "features/Charting/hooks/useChartingColors";
import useChartTooltipManager from "components/charting/hooks/useChartTooltipManager";
import { alpha } from "@mui/material/styles";
import useChartSelections from "components/charting/hooks/useChartSelections";
import type { Exercise, Variants } from "types";
import OneRepMaxEstimateTooltip from "./OneRepMaxEstimateTooltip";
import { useTranslation } from "react-i18next";
import { useUnitFormatter } from "hooks";
import useProfilingRepMetric from "../hooks/useProfilingRepMetric";

interface AthleteProfilingChartOneRepMaxEstimateLineProps {
  estimatedOneRepMax: number | null;
  /** The velocity the estimate is plotted at, on its line. */
  estimatedVelocity: number;
  yScale: ScaleLinear<number, number, never>;
  xScale: ScaleLinear<number, number, never>;
  minimumVelocityThreshold?: number | null;
  /** The athlete's own MVT when their profile's 1RM was raised past the exercise's threshold. */
  impliedMvt?: number;
  entityId: string;
  /** The side the estimate belongs to; only that side's 1RM can be updated from it. */
  variant?: Variants;
  /** Leaves out the dashed threshold line when another estimate already draws it. */
  hideThreshold?: boolean;
}

function AthleteProfilingChartOneRepMaxEstimateLine(props: AthleteProfilingChartOneRepMaxEstimateLineProps) {
  const {
    estimatedOneRepMax,
    estimatedVelocity,
    xScale,
    yScale,
    minimumVelocityThreshold,
    impliedMvt,
    entityId,
    variant,
    hideThreshold,
  } = props;
  const { theme, axis } = useChartingColors();
  const selections = useChartSelections();
  const exerciseId = selections.measures[0]?.id;
  const exerciseVariants = (selections.measures[0]?.metadata as Exercise).variants;
  const variants = useMemo(() => (variant ? [variant] : exerciseVariants), [exerciseVariants, variant]);
  const athleteId = selections.sources[0].id;
  const { t } = useTranslation();
  const formatUnit = useUnitFormatter();
  const repMetric = useProfilingRepMetric();
  const formattedMinimumVelocityThreshold = formatUnit(minimumVelocityThreshold, repMetric) ?? "";
  const formattedImpliedMinimumVelocityThreshold =
    impliedMvt !== undefined ? formatUnit(impliedMvt, repMetric) : undefined;

  const x = useMemo(() => (estimatedOneRepMax ? xScale(estimatedOneRepMax) : 0), [estimatedOneRepMax, xScale]);
  const y = useMemo(() => yScale(estimatedVelocity), [estimatedVelocity, yScale]);
  const thresholdY = useMemo(
    () => (minimumVelocityThreshold ? yScale(minimumVelocityThreshold) : 0),
    [minimumVelocityThreshold, yScale]
  );
  const { open, onMouseEnter, onMouseLeave, onClick, ChartTooltipPortal, onClose } = useChartTooltipManager(
    useMemo(() => ({ x, y }), [x, y])
  );
  const getX = useCallback((_: unknown, index: number) => xScale.range()[index], [xScale]);

  if (!estimatedOneRepMax || !minimumVelocityThreshold || !exerciseId) {
    return null;
  }

  return (
    <Fragment>
      <ChartTooltipPortal>
        <OneRepMaxEstimateTooltip
          entityId={entityId}
          athleteId={athleteId}
          exerciseId={exerciseId}
          variants={variants}
          estimatedOneRepMax={estimatedOneRepMax}
          minimumVelocityThreshold={formattedMinimumVelocityThreshold}
          impliedMvt={formattedImpliedMinimumVelocityThreshold}
          onSuccess={onClose}
        />
      </ChartTooltipPortal>
      {!hideThreshold && (
        <Fragment>
          <text y={thresholdY - 5} x={xScale.range()[0]} fontSize={12} fill={axis.line}>
            {t("charting.minimum_velocity_threshold_formatted", {
              minimumVelocityThreshold: formattedMinimumVelocityThreshold,
            })}
          </text>
          <LinePath
            data={[thresholdY, thresholdY]}
            x={getX}
            y={(yPoint) => yPoint}
            stroke={theme.palette.secondary.main}
            strokeDasharray={4}
            strokeWidth={1.5}
            strokeLinecap="round"
          />
        </Fragment>
      )}
      <g transform={`translate(${x},${y})`}>
        <circle
          key="MVTLine-circle-outer"
          cursor="pointer"
          r={16}
          fill="transparent"
          strokeWidth={2}
          strokeDasharray={4}
          stroke={alpha(theme.palette.secondary.main, open ? 1 : 0)}
        />
        <circle cursor="pointer" r={8} strokeWidth={2} fill={theme.palette.secondary.main} />
        {variant && (
          <text
            fontSize={12}
            fontWeight={600}
            y={1}
            textAnchor="middle"
            dominantBaseline="middle"
            fill={theme.palette.secondary.contrastText}
            pointerEvents="none"
          >
            {t("measures.variant_single_letter", { context: variant })}
          </text>
        )}
        <circle
          cursor="pointer"
          r={8}
          fill="transparent"
          onClick={onClick}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        />
      </g>
    </Fragment>
  );
}

export default AthleteProfilingChartOneRepMaxEstimateLine;
